'use client'

import { imageQuality } from '@repo/sanity/image'
import { getDiscount, getPriceAmount, getPriceCurrency } from '@repo/shopify/price'
import { Product } from '@repo/shopify/types'
import { Button } from '@repo/ui/components/button'
import PriceWithDiscount from '@repo/ui/components/price-with-discount'
import { motion, useMotionValueEvent, useScroll } from 'motion/react'
import { Image } from 'next-sanity/image'
import { FunctionComponent, useEffect, useRef, useState } from 'react'
import { useDialog } from '../../../providers/DialogProvider'
import { useProduct } from '../../../providers/ProductProvider'
import { Dictionary } from '../../../services/dictionary.service'
import { ProductDto } from '../../../types/product.types'

interface Props {
  productDto: ProductDto
  shopifyProduct: Product
  dictionary: Dictionary
}

const ProductStickyAddToCartPanel: FunctionComponent<Props> = ({ productDto, shopifyProduct, dictionary }) => {
  const { scrollY } = useScroll()
  const { openDialogIds } = useDialog()
  const { galleryRef, goToAddToCartButton } = useProduct()
  const [hidden, setHidden] = useState(true)
  const ref = useRef<HTMLDivElement>(null)
  const title = String(productDto?.sanityProduct?.title)
  const image = productDto?.sanityProduct?.images?.[0]
  const amount = getPriceAmount(shopifyProduct)
  const currencyCode = getPriceCurrency(shopifyProduct)
  const discount = getDiscount(shopifyProduct)

  // There is a certain vertical 'window' we want to show the panel.
  const galleryHeight = galleryRef?.current?.offsetHeight || 10000
  const panelHeight = ref?.current?.offsetHeight || 200
  const panelTopOffset = ref?.current?.getBoundingClientRect().top || 1000
  const trackHeight = 2000
  const from = galleryHeight - panelTopOffset + panelHeight
  const to = from + trackHeight

  useMotionValueEvent(scrollY, 'change', (latest: number) => {
    if (openDialogIds.size > 0) {
      // Do not show if there are other dialogs open.
      setHidden(true)
    } else if (scrollY.getPrevious() === 0) {
      // We haven't scrolled yet; gives a better
      // UX to not show the panel yet.
      setHidden(true)
    } else {
      setHidden(latest < from || latest > to)
    }
  })

  // Prevent showing on page load.
  useEffect(() => {
    if (openDialogIds.size > 0) {
      setHidden(true)
    }
  }, [openDialogIds])

  if (!shopifyProduct.availableForSale) {
    return null
  }

  return (
    <motion.aside
      ref={ref}
      initial='hidden'
      variants={{
        visible: { transform: 'translate3d(0, 0, 0)' },
        hidden: { transform: 'translate3d(110%, 0, 0)' },
      }}
      transition={{
        duration: 0.2,
        ease: 'easeOut',
      }}
      role='dialog'
      inert={hidden}
      animate={hidden ? 'hidden' : 'visible'}
      className='right-miista-page-padding border-border bg-popover text-popover-foreground h-(--details-panel-height) w-(--section-max-width) fixed bottom-[var(--spacing-miista-bottom-offset,--spacing-miista-page-padding)] hidden items-stretch justify-between gap-3 overflow-hidden rounded-lg border border-solid p-3 [mask-image:radial-gradient(circle_0.6rem_at_calc(100%_-_1.2rem)_1.2rem,_transparent_100%,_black_100%)] lg:flex'
    >
      {image && (
        <figure className='relative aspect-[2/3] w-[7rem]' onClick={() => goToAddToCartButton(true)}>
          <Image
            className='rounded-lg object-cover'
            fill={true}
            priority={false}
            sizes='7rem'
            quality={imageQuality}
            src={image.asset?.url || ''}
            alt={image?.alt || title}
            blurDataURL={image.asset?.metadata?.lqip ?? undefined}
            placeholder={image.asset?.metadata?.lqip ? 'blur' : undefined}
          />
        </figure>
      )}
      <div className='flex flex-1 shrink-0 flex-col justify-between'>
        <h2 className='uppercase'>{title}</h2>
        <div className='items-end-safe flex justify-between'>
          <PriceWithDiscount amount={amount} discount={discount} currencyCode={currencyCode} />
          <Button type='button' variant='defaultStatic' onClick={() => goToAddToCartButton(true)}>
            {dictionary?.product?.addtobag || 'Add to bag'}
          </Button>
        </div>
      </div>
    </motion.aside>
  )
}

export default ProductStickyAddToCartPanel
